import React, { useEffect, useState } from 'react'
import { ProfileUser } from '../../Services/Operations/ProductAPI'
import { useDispatch, useSelector } from 'react-redux'

import { motion, AnimatePresence } from "motion/react";
import { useNavigate } from 'react-router-dom';
import { logout } from '../../Store/authSlice';
import { toast } from 'react-toastify';

// ── Mock API data ─────────────────────────────────────────────
const PROFILE = {
  id: 5,
  full_name: "User  user",
  email: "user@gmail.com",
  phone: "7894152603",
  birth_date: "1997-07-10T00:00:00.000000Z",
  created_at: "2026-04-26T10:55:09.000000Z",
  profile_image: null,
};

// ── Helpers ───────────────────────────────────────────────────
function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit", month: "long", year: "numeric",
  });
}
function formatJoined(iso) {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}
function getInitials(name) {
  return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
}
function calcAge(iso) {
  const birth = new Date(iso);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (today < new Date(today.getFullYear(), birth.getMonth(), birth.getDate())) age--;
  return age;
}

// ── Icons ─────────────────────────────────────────────────────
const Icons = {
  mail: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>,
  phone: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.06 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16l.92.92z"/></svg>,
  cake: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8"/><path d="M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2 1 2 1"/><path d="M2 21h20"/><path d="M7 8v2"/><path d="M12 8v2"/><path d="M17 8v2"/><path d="M7 4h.01"/><path d="M12 4h.01"/><path d="M17 4h.01"/></svg>,
  calendar: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  id: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
  edit: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  logout: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  camera: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>,
  check: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
};

// ── Animation variants ────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.09, duration: 0.5, ease: [0.22, 1, 0.36, 1] } }),
};
const fadeIn = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.4 } },
};

// ── Palette ───────────────────────────────────────────────────
const C = {
  bg: "#f4f3f1",
  card: "#ffffff",
  brown: "#3b2314",
  brownMid: "#5c3820",
  brownLight: "#7a4f35",
  brownTint: "#f7f0eb",
  brownBorder: "#e8ddd6",
  grey: "#e8e8e8",
  greyMid: "#9ca3af",
  text: "#1a1008",
  textMuted: "#7a6c63",
};

// ── Reusable InfoRow ──────────────────────────────────────────
function InfoRow({ icon, label, value, delay }) {
  return (
    <motion.div
      variants={fadeUp}
      custom={delay}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "14px",
        padding: "16px 0",
        borderBottom: `1px solid ${C.brownBorder}`,
      }}
    >
      <div style={{
        width: "36px", height: "36px", borderRadius: "10px",
        background: C.brownTint,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: C.brownLight, flexShrink: 0,
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: "11px", fontWeight: "600", color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", margin: "0 0 2px" }}>{label}</p>
        <p style={{ fontSize: "15px", fontWeight: "500", color: C.text, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</p>
      </div>
    </motion.div>
  );
}

// ── LogoutModal ───────────────────────────────────────────────
function LogoutModal({ onConfirm, onCancel }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed", inset: 0, background: "rgba(20,10,5,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 100, padding: "1rem",
      }}
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.88, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0, transition: { type: "spring", stiffness: 320, damping: 26 } }}
        exit={{ scale: 0.92, opacity: 0, y: 10 }}
        onClick={e => e.stopPropagation()}
        style={{
          background: C.card, borderRadius: "20px",
          padding: "36px 32px 28px",
          width: "100%", maxWidth: "360px",
          boxShadow: "0 24px 60px rgba(59,35,20,0.22), 0 4px 16px rgba(0,0,0,0.08)",
          textAlign: "center",
        }}
      >
        <div style={{
          width: "52px", height: "52px", borderRadius: "50%",
          background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 18px", color: "#dc2626",
        }}>
          {Icons.logout}
        </div>
        <h3 style={{ fontSize: "18px", fontWeight: "700", color: C.text, margin: "0 0 8px" }}>Sign out?</h3>
        <p style={{ fontSize: "14px", color: C.textMuted, margin: "0 0 28px", lineHeight: 1.6 }}>
          You will be returned to the login screen. Any unsaved changes will be lost.
        </p>
        <div style={{ display: "flex", gap: "10px" }}>
          <button onClick={onCancel} style={{
            flex: 1, height: "44px", borderRadius: "10px",
            border: `1.5px solid ${C.brownBorder}`, background: "transparent",
            fontSize: "14px", fontWeight: "600", color: C.textMuted, cursor: "pointer",
            fontFamily: "inherit",
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            flex: 1, height: "44px", borderRadius: "10px",
            border: "none", background: "#dc2626",
            fontSize: "14px", fontWeight: "600", color: "#fff", cursor: "pointer",
            fontFamily: "inherit",
          }}>Sign out</button>
        </div>
      </motion.div>
    </motion.div>
  );
}


const Profile = () => {
  const navigate=useNavigate()
   const { isAuthenticated ,token} = useSelector((state) => state.auth);
   const dispatch=useDispatch()

 

 const[p,setP]=useState(PROFILE)
  const [showLogout, setShowLogout] = useState(false);
  const [loggedOut, setLoggedOut] = useState(false);

  const handleLogout = () => { setShowLogout(false); setLoggedOut(true);
        dispatch(logout())
        toast.success("Loggedout successfully")
        navigate("/")
   };

  const handleGetProfile=async()=>{
    try {
        const res= await ProfileUser(token)
        console.log(res);
        if(res.status===200){
            setP(res.data.data)
        }
        
    } catch (error) {
        console.log(error);
        
    }
  }

  useEffect(() => {
    handleGetProfile()
  }, [])
  

  return (
    <div style={{
      minHeight: "100vh",
      background: C.bg,
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Playfair+Display:wght@600;700&display=swap" rel="stylesheet" />

      <AnimatePresence>
        {showLogout && <LogoutModal onConfirm={handleLogout} onCancel={() => setShowLogout(false)} />}
      </AnimatePresence>

      {loggedOut ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "16px" }}
        >
          <div style={{ width: "60px", height: "60px", borderRadius: "50%", background: C.brownTint, display: "flex", alignItems: "center", justifyContent: "center", color: C.brownLight }}>
            {Icons.check}
          </div>
          <p style={{ fontSize: "18px", fontWeight: "700", color: C.text, margin: 0 }}>Signed out successfully</p>
          <p style={{ fontSize: "14px", color: C.textMuted, margin: 0 }}>See you next time, {p.full_name.split(" ")[0]}.</p>
        </motion.div>
      ) : (
        <>
          {/* ── Top bar ── */}
          <motion.header
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } }}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "0 28px", height: "64px",
              background: "#fff",
              borderBottom: `1px solid ${C.brownBorder}`,
              position: "sticky", top: 0, zIndex: 10,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div onClick={() => navigate(-1)}
              style={{
                width: "32px", height: "32px", borderRadius: "8px",
                background: C.brown, display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <svg
  width="20"
  height="20"
  viewBox="0 0 24 24"
  fill="none"
>
  <path
    d="M19 12H5"
    stroke="white"
    strokeWidth="2"
    strokeLinecap="round"
  />
  <path
    d="M12 19l-7-7 7-7"
    stroke="white"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  />
</svg>
              </div>
              <span style={{ fontSize: "15px", fontWeight: "700", color: C.text, letterSpacing: "-0.3px" }}>Back</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "13px", color: C.textMuted, fontWeight: "500" }}>My Profile</span>
              <div style={{ width: "5px", height: "5px", borderRadius: "50%", background: C.brownBorder }} />
              <span style={{ fontSize: "13px", color: C.brown, fontWeight: "600" }}>#{p.id.toString().padStart(4, "0")}</span>
            </div>
          </motion.header>

          {/* ── Content ── */}
          <div style={{
            maxWidth: "680px",
            margin: "0 auto",
            padding: "36px 20px 60px",
          }}>

            {/* ── Hero card ── */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={0}
              style={{
                background: C.brown,
                borderRadius: "24px",
                padding: "36px 32px 32px",
                marginBottom: "20px",
                position: "relative",
                overflow: "hidden",
                boxShadow: "0 12px 40px rgba(59,35,20,0.28), 0 2px 8px rgba(59,35,20,0.12)",
              }}
            >
              {/* Decorative circles */}
              <div style={{ position: "absolute", top: "-40px", right: "-40px", width: "180px", height: "180px", borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
              <div style={{ position: "absolute", bottom: "-30px", right: "60px", width: "120px", height: "120px", borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />

              <div style={{ display: "flex", alignItems: "flex-start", gap: "20px", position: "relative" }}>
                {/* Avatar */}
                <motion.div
                  initial={{ scale: 0.7, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1, transition: { delay: 0.15, type: "spring", stiffness: 260, damping: 20 } }}
                  style={{ position: "relative", flexShrink: 0 }}
                >
                  <div style={{
                    width: "84px", height: "84px", borderRadius: "50%",
                    background: "rgba(255,255,255,0.15)",
                    border: "2.5px solid rgba(255,255,255,0.25)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "28px", fontWeight: "700", color: "#fff",
                    fontFamily: "'Playfair Display', serif",
                    letterSpacing: "-1px",
                  }}>
                    {getInitials(p.full_name)}
                  </div>
                  {/* Camera badge */}
                  <motion.button
                    whileHover={{ scale: 1.12 }}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      position: "absolute", bottom: 0, right: 0,
                      width: "26px", height: "26px", borderRadius: "50%",
                      background: "#fff", border: "none", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: C.brown, boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    }}
                  >
                    {Icons.camera}
                  </motion.button>
                </motion.div>

                {/* Name & badges */}
                <div style={{ flex: 1, paddingTop: "4px" }}>
                  <motion.h1
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0, transition: { delay: 0.2, duration: 0.5 } }}
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: "26px", fontWeight: "700",
                      color: "#fff", margin: "0 0 6px",
                      letterSpacing: "-0.5px",
                    }}
                  >
                    {p.full_name}
                  </motion.h1>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { delay: 0.3 } }}
                    style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}
                  >
                    <span style={{
                      fontSize: "12px", fontWeight: "600",
                      color: "rgba(255,255,255,0.75)",
                      background: "rgba(255,255,255,0.12)",
                      borderRadius: "20px", padding: "4px 12px",
                      letterSpacing: "0.04em",
                    }}>
                      Member
                    </span>
                    <span style={{
                      fontSize: "12px", fontWeight: "600",
                      color: "rgba(255,255,255,0.6)",
                    }}>
                      Age {calcAge(p.birth_date)}
                    </span>
                  </motion.div>
                </div>

                {/* Edit button */}
                <motion.button
                  whileHover={{ scale: 1.06, background: "rgba(255,255,255,0.2)" }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    flexShrink: 0,
                    display: "flex", alignItems: "center", gap: "6px",
                    background: "rgba(255,255,255,0.12)",
                    border: "1.5px solid rgba(255,255,255,0.2)",
                    borderRadius: "10px", padding: "8px 14px",
                    color: "#fff", fontSize: "13px", fontWeight: "600",
                    cursor: "pointer", fontFamily: "inherit",
                    transition: "background 0.2s",
                  }}
                >
                  {Icons.edit}
                  Edit
                </motion.button>
              </div>

              {/* Joined strip */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0, transition: { delay: 0.35, duration: 0.4 } }}
                style={{
                  marginTop: "24px",
                  paddingTop: "18px",
                  borderTop: "1px solid rgba(255,255,255,0.12)",
                  display: "flex", alignItems: "center", gap: "8px",
                  color: "rgba(255,255,255,0.55)", fontSize: "13px",
                }}
              >
                {Icons.calendar}
                <span>Member since <strong style={{ color: "rgba(255,255,255,0.85)", fontWeight: "600" }}>{formatJoined(p.created_at)}</strong></span>
              </motion.div>
            </motion.div>

            {/* ── Info card ── */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={2}
              style={{
                background: C.card,
                borderRadius: "20px",
                padding: "8px 24px 4px",
                marginBottom: "16px",
                boxShadow: "0 4px 20px rgba(59,35,20,0.07), 0 1px 4px rgba(0,0,0,0.04)",
                border: `1px solid ${C.brownBorder}`,
              }}
            >
              <motion.p
                variants={fadeUp} custom={2}
                style={{ fontSize: "11px", fontWeight: "700", color: C.textMuted, letterSpacing: "0.1em", textTransform: "uppercase", margin: "18px 0 0" }}
              >
                Personal information
              </motion.p>
              <motion.div variants={{ show: { transition: { staggerChildren: 0.07 } } }} initial="hidden" animate="show">
                <InfoRow icon={Icons.id}       label="Account ID"    value={`#${p.id.toString().padStart(4, "0")}`}  delay={3} />
                <InfoRow icon={Icons.mail}     label="Email address" value={p.email}                                  delay={4} />
                <InfoRow icon={Icons.phone}    label="Phone number"  value={p.phone}                                  delay={5} />
                <InfoRow icon={Icons.cake}     label="Date of birth" value={formatDate(p.birth_date)}                 delay={6} />
                <InfoRow icon={Icons.calendar} label="Member since"  value={formatJoined(p.created_at)}               delay={7} />
              </motion.div>
            </motion.div>

            {/* ── Stats strip ── */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={8}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              {[
                { label: "User ID", value: `#${p.id}` },
                { label: "Age", value: `${calcAge(p.birth_date)} yrs` },
                { label: "Status", value: "Active" },
              ].map((stat, i) => (
                <motion.div
                  key={stat.label}
                  whileHover={{ y: -3, boxShadow: "0 8px 24px rgba(59,35,20,0.12)" }}
                  style={{
                    background: C.card,
                    borderRadius: "16px",
                    padding: "18px 16px",
                    textAlign: "center",
                    border: `1px solid ${C.brownBorder}`,
                    boxShadow: "0 2px 8px rgba(59,35,20,0.05)",
                    transition: "box-shadow 0.2s",
                    cursor: "default",
                  }}
                >
                  <p style={{ fontSize: "11px", fontWeight: "600", color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", margin: "0 0 6px" }}>{stat.label}</p>
                  <p style={{ fontSize: "18px", fontWeight: "700", color: stat.label === "Status" ? "#16a34a" : C.brown, margin: 0, letterSpacing: "-0.5px", fontFamily: "'Playfair Display', serif" }}>{stat.value}</p>
                </motion.div>
              ))}
            </motion.div>

            {/* ── Logout button ── */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={9}
            >
              <motion.button
                whileHover={{ scale: 1.015, boxShadow: "0 8px 28px rgba(220,38,38,0.18)" }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowLogout(true)}
                style={{
                  width: "100%",
                  height: "52px",
                  border: "1.5px solid #fecaca",
                  borderRadius: "14px",
                  background: "#fff5f5",
                  color: "#dc2626",
                  fontSize: "15px",
                  fontWeight: "700",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "9px",
                  fontFamily: "inherit",
                  letterSpacing: "0.01em",
                  transition: "box-shadow 0.2s",
                }}
              >
                {Icons.logout}
                Sign out
              </motion.button>
            </motion.div>

          </div>
        </>
      )}
    </div>
  );
}

export default Profile
